import axios from "axios";

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

/**
 * Runs context-based rules validation for a loan.
 * @param {Object} params
 * @param {string} params.loanId - The loan identifier
 * @param {string} params.contextName - The ruleset context name (e.g. "CU_FINAL_DETERMINATION")
 * @param {Array<{id: string, value: string}>} params.fields - Fields to validate
 * @returns {Promise<Object>} Rules result with overallStatus and groups
 */
export const runRules = async ({ loanId, contextName, showFatalAsWarning, fields, requestedBy }) => {
    const url = `${API_DOMAIN}/api/rules/run-rules/${encodeURIComponent(loanId)}`;
    const payload = { contextName, showFatalAsWarning, fields, requestedBy };

    try {
        const response = await axios.post(url, payload, {
            headers: { "Content-Type": "application/json" }
        });
        if (response.status === 200) {
            return response.data;
        }
        throw new Error(`Unexpected status: ${response.status}`);
    } catch (error) {
        console.error("Error running rules:", error);
        throw error;
    }
};

/**
 * Runs rules validation for multiple contexts in one call.
 * @param {Object} params
 * @param {string} params.loanId - The loan identifier
 * @param {Array<string>} params.contextName - Ruleset context names
 * @param {Array<{id: string, value: string}>} params.fields - Fields to validate
 * @param {string} [params.errorType] - Optional severity hint for backend routing
 * @param {string} [params.requestedBy] - User executing the call
 * @returns {Promise<Object>} Rules result with overallStatus and groups
 */
export const runRulesMulti = async ({ loanId, contextName, ruleId, fields = [], showFatalAsWarning, requestedBy }) => {
    const url = `${API_DOMAIN}/api/rules/run-rules-multi/${encodeURIComponent(loanId)}`;

    const payload = {
        contextName: Array.isArray(contextName) ? contextName : [],
        ruleId: ruleId,
        showFatalAsWarning,
        fields,
        requestedBy
    };

    try {
        const response = await axios.post(url, payload, {
            headers: { "Content-Type": "application/json" }
        });
        if (response.status === 200) {
            return response.data;
        }
        throw new Error(`Unexpected status: ${response.status}`);
    } catch (error) {
        console.error("Error running multi-context rules:", error);
        throw error;
    }
};

/**
 * Calls the backend to run milestone rules for a loan.
 * @param {Object} params
 * @param {string} params.milestoneName
 * @param {string} params.loanId
 * @param {string} params.executedBy
 * @param {string} params.authCode
 * @returns {Promise<Array>} List of audit results
 */
export const runMilestoneRules = async ({ channelName, milestoneName, loanId, executedBy }) => {
    const url = `${API_DOMAIN}/api/rules/milestones/run`;
    const payload = {
        ChannelName: channelName,
        MilestoneName: milestoneName,
        LoanId: loanId,
        ExecutedBy: executedBy
    };
    try {
        const response = await axios.post(url, payload, {
            headers: { "Content-Type": "application/json" }
        });
        if (response.status === 200) {
            return response.data;
        }
        throw new Error(`Unexpected status: ${response.status}`);
    } catch (error) {
        console.error("Error running milestone rules:", error);
        throw error;
    }
};


/**
 * Calls backend to save milestone override history.
 * @param {Object} params
 * @param {number} params.overrideId
 * @param {string} params.milestoneName
 * @param {number} params.ruleId
 * @param {string} params.loanId
 * @param {string} params.overrideReason
 * @param {string|null} params.detailsJson
 * @param {string} params.performedBy
 * @param {string} params.performedAt (ISO string)
 * @returns {Promise<number>} Override Id
 */
export const saveMilestoneOverride = async ({
    overrideId,
    milestoneName,
    ruleId,
    loanId,
    overrideReason,
    detailsJson,
    performedBy,
    performedAt
}) => {

    const url = `${API_DOMAIN}/api/rules/override`;

    const payload = {
        OverrideId: overrideId,
        MilestoneName: milestoneName,
        RuleId: ruleId,
        LoanId: loanId,
        OverrideReason: overrideReason,
        DetailsJson: detailsJson,
        PerformedBy: performedBy,
        PerformedAt: performedAt
    };

    try {
        const response = await axios.post(url, payload, {
            headers: { "Content-Type": "application/json" }
        });

        if (response.status === 200) {
            return response.data; // returns long id
        }

        throw new Error(`Unexpected status: ${response.status}`);
    } catch (error) {
        console.error("Error saving milestone override:", error);
        throw error;
    }
};